import React, { useState, useRef } from 'react';
import { BadgeCheck, Download, X, Linkedin, Twitter, Link, Camera } from 'lucide-react';
import html2canvas from 'html2canvas';

const colorSchemes = [
    { 
        primary: '#fff', 
        secondary: '#0284C7', 
        accent: '#0369A1',
        border: '#38BDF8',
        text: '#075985'
      },
  { 
    primary: '#fff', 
    secondary: '#DC2626', 
    accent: '#991B1B',
    border: '#F87171',
    text: '#7F1D1D'
  },
  { 
    primary: '#fff', 
    secondary: '#2563EB', 
    accent: '#1D4ED8',
    border: '#60A5FA',
    text: '#1E3A8A'
  },
  { 
    primary: '#fff', 
    secondary: '#7C3AED', 
    accent: '#5B21B6',
    border: '#A78BFA',
    text: '#4C1D95'
  },
  { 
    primary: '#fff', 
    secondary: '#059669', 
    accent: '#047857',
    border: '#34D399',
    text: '#065F46'
  },
 
];

const AlmaCard = ({ profile, colorScheme, cardRef }) => {
    return (
      <div 
        ref={cardRef}
        className="w-[400px] h-[500px] relative rounded-xl overflow-hidden bg-white"
        style={{ 
          boxShadow: `0 4px 6px -1px ${colorScheme.secondary}20, 0 2px 4px -2px ${colorScheme.secondary}10`
        }}
      >
        {/* Top Color Bar */}
        <div 
          className="h-24"
          style={{ 
            background: `linear-gradient(90deg, ${colorScheme.secondary}, ${colorScheme.accent})`
          }}
        />
  
        <div className="relative px-6 -mt-16">
          {/* Header with Logos in a white background card */}
          <div className="bg-white rounded-xl p-4 mb-6">
            <div className="flex justify-between items-center">
              <img 
                src="https://i.ibb.co/Y2zGN8s/saarthilogo.png"
                alt="Saarthi"
                className="h-12 object-contain"
              />
              <img 
                src={profile.college.logo || "https://i.ibb.co/7vZw4Vp/nitk-logo.png"}
                alt="College Logo"
                className="h-12 w-12 object-contain"
              />
            </div>
          </div>
  
          {/* College Name */}
          <div className="text-center mb-6">
            <h3 
              className="text-xl font-semibold tracking-wide uppercase"
              style={{ color: colorScheme.text }}
            >
              {profile.college.name || "NATIONAL INSTITUTE OF TECHNOLOGY"}
            </h3>
          </div>
  
          {/* Rest of the content remains the same */}
          <div className="flex gap-4">
            {profile.personalInfo?.avatar ? (
              <img
                src={profile.personalInfo.avatar}
                alt={profile.firstName}
                className="w-28 h-28 rounded-lg object-cover ring-2"
                style={{ ringColor: colorScheme.border }}
              />
            ) : (
              <div 
                className="w-28 h-28 rounded-lg flex items-center justify-center"
                style={{ 
                  backgroundColor: `${colorScheme.secondary}10`,
                  color: colorScheme.text
                }}
              >
                <span className="text-3xl font-bold">
                  {profile.firstName?.[0]}
                  {profile.lastName?.[0]}
                </span>
              </div>
            )}
            
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h2 
                  className="text-2xl font-bold tracking-tight"
                  style={{ color: colorScheme.text }}
                >
                  {profile.firstName} {profile.lastName}
                </h2>
                {profile.isVerified && (
                  <BadgeCheck 
                    className="h-5 w-5"
                    style={{ color: colorScheme.secondary }}
                  />
                )}
              </div>
              <div 
                className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-sm"
                style={{ 
                  backgroundColor: `${colorScheme.secondary}10`,
                  color: colorScheme.text
                }}
              >
                Class of {profile.graduationYear}
              </div>
              <div className="mt-4">
                <p className="text-gray-600">{profile.academicInfo?.degree}</p>
                <p className="text-gray-500">{profile.academicInfo?.department}</p>
              </div>
            </div>
          </div>
  
          <div 
            className="my-6 h-px w-full"
            style={{ backgroundColor: `${colorScheme.secondary}20` }}
          />
  
          <div className="flex justify-between items-end">
            <div 
              className="p-2 rounded-lg"
              style={{ backgroundColor: `${colorScheme.secondary}10` }}
            >
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(
                  window.location.origin + '/alumni/' + profile._id
                )}&bgcolor=FFFFFF&color=${encodeURIComponent(colorScheme.text.substring(1))}`}
                alt="Profile QR"
                className="w-20 h-20"
              />
            </div>
            <div className="text-right">
              <span 
                className="text-sm font-medium block mb-1"
                style={{ color: colorScheme.text }}
              >
                Alumni ID
              </span>
              <span className="font-mono text-gray-600">
                {profile.enrollmentNumber}
              </span>
            </div>
          </div>
        </div>
  
        {/* Bottom Color Bar */}
        <div 
          className="absolute bottom-0 w-full h-6"
          style={{ 
            background: `linear-gradient(90deg, ${colorScheme.secondary}, ${colorScheme.accent})`
          }}
        />
      </div>
    );
  };

// ... ShareModal and other components remain the same ...

const ShareModal = ({ isOpen, onClose, cardRef, profile }) => {
  const [pdfBlob, setPdfBlob] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const generatePDF = async () => {
    if (!cardRef.current || isGenerating) return;
    
    setIsGenerating(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        logging: false,
        useCORS: true,
        backgroundColor: null
      });

      // Import jsPDF dynamically to avoid SSR issues
      const { jsPDF } = await import('jspdf');
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: [400, 500]
      });
      
      pdf.addImage(imgData, 'PNG', 0, 0, 400, 500);
      const blob = pdf.output('blob');
      setPdfBlob(blob);
      
      // Trigger download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${profile.firstName}-${profile.lastName}-alma-card.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const shareToLinkedIn = async () => {
    if (!pdfBlob) await generatePDF();
    // LinkedIn's API doesn't directly support file sharing, so we'll share a message
    const text = `Check out my Alma Card from ${profile.academicInfo?.department}!`;
    const url = `https://www.linkedin.com/sharing/share-offsite/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const shareToTwitter = async () => {
    if (!pdfBlob) await generatePDF();
    // Twitter's API doesn't directly support file sharing, so we'll share a message
    const text = `Check out my Alma Card from ${profile.academicInfo?.department}!`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const copyToClipboard = async () => {
    if (!pdfBlob) await generatePDF();
    try {
      const file = new File([pdfBlob], 'alma-card.pdf', { type: 'application/pdf' });
      await navigator.clipboard.write([
        new ClipboardItem({
          [file.type]: file
        })
      ]);
      alert('PDF copied to clipboard!');
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      alert('Unable to copy PDF directly. Please use the download button.');
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Share Your Card</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex flex-col gap-3">
          <button
            onClick={generatePDF}
            disabled={isGenerating}
            className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            {isGenerating ? 'Generating PDF...' : 'Download PDF'}
          </button>
          <button
            onClick={shareToLinkedIn}
            className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Linkedin className="h-4 w-4" />
            Share on LinkedIn
          </button>
          <button
            onClick={shareToTwitter}
            className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600"
          >
            <Twitter className="h-4 w-4" />
            Share on Twitter
          </button>
          <button
            onClick={copyToClipboard}
            className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <Link className="h-4 w-4" />
            Copy PDF to Clipboard
          </button>
        </div>
      </div>
    </div>
  );
};

const AlmaCardModal = ({ isOpen, onClose, profile }) => {
  const [currentScheme, setCurrentScheme] = useState(colorSchemes[0]);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const cardRef = useRef(null);

  if (!isOpen || !profile) return null;

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-lg flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-xl flex flex-col items-center">
        <div className="flex gap-4 mb-6">
          <div className="flex gap-2">
            {colorSchemes.map((scheme, index) => (
              <button
                key={index}
                onClick={() => setCurrentScheme(scheme)}
                className="w-8 h-8 rounded-full transition-transform hover:scale-110"
                style={{ 
                  background: `linear-gradient(135deg, ${scheme.primary}, ${scheme.secondary})`,
                  border: currentScheme === scheme ? '2px solid white' : 'none'
                }}
              />
            ))}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setIsShareModalOpen(true)}
              className="p-2 bg-white/10 rounded-lg hover:bg-white/20"
            >
              <Download className="h-5 w-5 text-white" />
            </button>
            <button
              onClick={onClose}
              className="p-2 bg-white/10 rounded-lg hover:bg-white/20"
            >
              <X className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>
        
        <AlmaCard 
          profile={profile}
          colorScheme={currentScheme}
          cardRef={cardRef}
        />

        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          cardRef={cardRef}
          profile={profile}
        />
      </div>
    </div>
  );
};


const AlmaCardButton = ({ profile }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg text-sm font-medium hover:from-purple-700 hover:to-blue-700 transition-all hover:scale-105"
      >
        <Camera className="h-4 w-4 mr-2" />
        View Alma Card
      </button>
      <AlmaCardModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        profile={profile}
      />
    </>
  );
};

export default AlmaCardButton;